import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, MapPin, Users, Sparkles, LayoutGrid, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { VenueAutocomplete } from '@/components/VenueAutocomplete';
import { eventService } from '@/lib/api/event.service';
import { venueService } from '@/lib/api/venue.service';
import { venueRoomService } from '@/lib/api/venue-room.service';
import { celebrationService } from '@/lib/api/celebration.service';
import { formatShortDate } from '@/utils/dateFormat';
import type { CreateEventDto, UpdateEventDto } from '@/types/event.types';
import { EventStatus, EventType } from '@/types/event.types';
import type { GooglePlaceSearchResult } from '@/types/venue.types';
import type { VenueRoomWithConfigs } from '@/types/venue-room.types';
import { SEATING_CONFIG_LABELS } from '@/types/venue-room.types';

interface EventFormProps {
  mode: 'create' | 'edit';
  celebrationIdOverride?: number;
  embedded?: boolean;
  onCancel?: () => void;
  onCreated?: () => void;
  onUpdated?: (eventId: number) => void;
}

interface ApiValidationError {
  field: string;
  message: string;
  constraint?: string;
}

interface ApiErrorResponse {
  message?: string;
  details?: {
    validationErrors?: ApiValidationError[];
  };
}

interface ApiError {
  response?: {
    status?: number;
    data?: ApiErrorResponse;
  };
}

type GooglePlaceWithDbId = GooglePlaceSearchResult & {
  _db_venue_id?: number;
};

export const EventForm: React.FC<EventFormProps> = ({
  mode,
  celebrationIdOverride,
  embedded = false,
  onCancel,
  onCreated,
  onUpdated,
}) => {
  const navigate = useNavigate();
  const { celebrationId, id } = useParams();
  const eventId = id; // Route uses :id param
  const { t, i18n } = useTranslation(['events', 'common']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [selectedVenue, setSelectedVenue] = useState<GooglePlaceSearchResult | null>(null);
  const [originalVenueId, setOriginalVenueId] = useState<number | undefined>();
  const [originalVenueTimezone, setOriginalVenueTimezone] = useState<string>('UTC');
  const [selectedVenueTimezone, setSelectedVenueTimezone] = useState<string>('UTC');
  const [venueRooms, setVenueRooms] = useState<VenueRoomWithConfigs[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<number | undefined>();
  const [selectedSeatingConfig, setSelectedSeatingConfig] = useState<string | undefined>();
  const [celebrationDetails, setCelebrationDetails] = useState<{ 
    start_date: string; 
    end_date: string;
    location?: { lat: number; lng: number; radius_miles?: number }
  } | null>(null);
  const [formData, setFormData] = useState<CreateEventDto & { status?: EventStatus; celebration_id?: number }>({
    title: '',
    description: '',
    event_type: EventType.OTHER,
    event_date: '', // Keep for backend compatibility but derive from start_time
    start_time: '',
    end_time: '',
    venue_id: undefined,
    is_public: true,
    max_guests: undefined,
    status: EventStatus.DRAFT,
    celebration_id: undefined
  });

  const routeCelebrationId = celebrationId ? parseInt(celebrationId, 10) : null;
  const parsedCelebrationId = celebrationIdOverride ?? routeCelebrationId;
  const isCelebrationSpecific = parsedCelebrationId !== null && !Number.isNaN(parsedCelebrationId);

  const loadCelebrationDetails = useCallback(async (celebrationId: number) => {
    try {
      const celebration = await celebrationService.getCelebration(celebrationId);
      setCelebrationDetails({
        start_date: celebration.start_date,
        end_date: celebration.end_date,
        location: (celebration.latitude && celebration.longitude) ? {
          lat: celebration.latitude,
          lng: celebration.longitude,
          radius_miles: celebration.radius_miles
        } : undefined
      });
    } catch (err) {
      console.error('Error loading celebration details:', err);
    }
  }, []);

  const loadVenueRooms = useCallback(async (venueId: number) => {
    try {
      const rooms = await venueRoomService.getRooms(venueId);
      setVenueRooms(rooms);
    } catch (err) {
      console.error('Failed to load venue rooms:', err);
      setVenueRooms([]);
    }
  }, []);

  const loadVenueDetails = useCallback(async (venueId: number) => {
    try {
      const venueDetails = await venueService.getVenueWithDetails(venueId);
      // Convert VenueWithGoogleDetails to GooglePlaceSearchResult format for VenueAutocomplete
      setSelectedVenue({
        place_id: venueDetails.google_place_id,
        name: venueDetails.name,
        formatted_address: venueDetails.formatted_address || venueDetails.address,
        types: venueDetails.types || venueDetails.place_types || [],
        rating: venueDetails.rating,
        user_ratings_total: venueDetails.user_ratings_total,
        geometry: venueDetails.geometry
      });

      return venueDetails;
    } catch (err) {
      console.error('Failed to load venue details:', err);
      // Don't clear venue_id if fetch fails - still keep the reference
      return null;
    }
  }, []);

  const formatWallClock = useCallback((isoString: string, timezone: string) => {
    return new Intl.DateTimeFormat(i18n.language, {
      timeZone: timezone,
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(isoString));
  }, [i18n.language]);

  const loadEvent = useCallback(async (eventId: number) => {
    try {
      setLoading(true);
      const event = await eventService.getEvent(eventId);

      // Helper: convert a Date to local 'YYYY-MM-DDTHH:mm' for datetime-local inputs
      const toLocalDateTimeString = (dateStr: string) => {
        const d = new Date(dateStr);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      };

      setFormData({
        title: event.title,
        description: event.description || '',
        event_type: event.event_type,
        event_date: event.event_date,
        start_time: event.start_time ? toLocalDateTimeString(event.start_time) : '',
        end_time: event.end_time ? toLocalDateTimeString(event.end_time) : '',
        venue_id: event.venue_id,
        is_public: event.is_public,
        max_guests: event.max_guests,
        status: event.status,
        celebration_id: event.celebration_id // Store celebration_id from the event
      });

      // Restore room and seating config selections
      if (event.venue_room_id) setSelectedRoomId(event.venue_room_id);
      if (event.seating_config) setSelectedSeatingConfig(event.seating_config);

      // Load rooms for this venue
      if (event.venue_id) {
        loadVenueRooms(event.venue_id);
        // Load and display current venue details and timezone context.
        const venueDetails = await loadVenueDetails(event.venue_id);
        const timezone = venueDetails?.timezone || 'UTC';
        setOriginalVenueTimezone(timezone);
        setSelectedVenueTimezone(timezone);
      } else {
        setOriginalVenueTimezone('UTC');
        setSelectedVenueTimezone('UTC');
      }

      setOriginalVenueId(event.venue_id);
      
      // Load celebration dates for validation
      if (event.celebration_id) {
        loadCelebrationDetails(event.celebration_id);
      }
      
      setError(null);
    } catch {
      setError(t('events:form.errors.failedLoad'));
    } finally {
      setLoading(false);
    }
  }, [loadCelebrationDetails, loadVenueDetails, loadVenueRooms, t]);

  useEffect(() => {
    if (mode === 'edit' && eventId) {
      loadEvent(parseInt(eventId));
    }
    if (parsedCelebrationId) {
      loadCelebrationDetails(parsedCelebrationId);
    }
  }, [mode, eventId, parsedCelebrationId, loadEvent, loadCelebrationDetails]);

  // Default event start/end times from celebration dates (create mode only)
  useEffect(() => {
    if (mode === 'create' && celebrationDetails && !formData.start_time && !formData.end_time) {
      const startDate = celebrationDetails.start_date?.split('T')[0];
      const endDate = celebrationDetails.end_date?.split('T')[0] || startDate;
      if (startDate) {
        setFormData(prev => ({
          ...prev,
          start_time: `${startDate}T09:00`,
          end_time: `${endDate}T17:00`,
          event_date: startDate,
        }));
      }
    }
  }, [mode, celebrationDetails, formData.start_time, formData.end_time]);

  const validateTimeRange = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.start_time) {
      errors.start_time = t('events:form.validation.startBeforeEnd');
    }
    
    if (!formData.end_time) {
      errors.end_time = t('events:form.validation.startBeforeEnd');
    }
    
    if (formData.start_time && formData.end_time) {
      const startTime = new Date(formData.start_time);
      const endTime = new Date(formData.end_time);
      
      if (startTime >= endTime) {
      errors.time_range = t('events:form.validation.startBeforeEnd');
      }
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateTimeRange()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      let venueId = formData.venue_id;

      if (selectedVenue && !venueId) {
        try {
          const venueResult = await venueService.ensureVenue(selectedVenue.place_id);
          venueId = venueResult.venue_id;
          // Load rooms for newly ensured venue
          loadVenueRooms(venueId);

          try {
            const venueDetails = await venueService.getVenueWithDetails(venueId);
            setSelectedVenueTimezone(venueDetails.timezone || 'UTC');
          } catch {
            setSelectedVenueTimezone('UTC');
          }
        } catch {
        setError(t('events:form.errors.failedVenue'));
          setLoading(false);
          return;
        }
      }

      const eventData = {
        ...formData,
        celebration_id: isCelebrationSpecific && parsedCelebrationId 
          ? parsedCelebrationId 
          : formData.celebration_id, // Use celebration_id from formData in edit mode
        venue_id: venueId,
        venue_room_id: selectedRoomId || undefined,
        seating_config: selectedSeatingConfig || undefined,
        // Derive event_date from start_time using local date (not UTC) to avoid day-shift
        event_date: formData.start_time
          ? (() => {
              const d = new Date(formData.start_time);
              const pad = (n: number) => String(n).padStart(2, '0');
              return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
            })()
          : new Date().toISOString().slice(0, 10),
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
        max_guests: formData.max_guests || undefined
      };

      // Only check for celebration_id in create mode
      if (mode === 'create' && !eventData.celebration_id) {
      setError(t('events:form.validation.noCelebration'));
        setLoading(false);
        return;
      }

      if (mode === 'edit' && eventId && originalVenueId !== undefined && venueId !== undefined && venueId !== originalVenueId) {
        let newTimezone = selectedVenueTimezone || 'UTC';
        try {
          const venueDetails = await venueService.getVenueWithDetails(venueId);
          newTimezone = venueDetails.timezone || 'UTC';
          setSelectedVenueTimezone(newTimezone);
        } catch {
          // Keep fallback timezone if venue detail lookup fails.
        }

        const oldTimezone = originalVenueTimezone || 'UTC';
        if (oldTimezone !== newTimezone) {
          const startIso = new Date(formData.start_time).toISOString();
          const endIso = new Date(formData.end_time).toISOString();
          const oldStart = formatWallClock(startIso, oldTimezone);
          const oldEnd = formatWallClock(endIso, oldTimezone);

          const confirmationMessage = t('events:form.timezoneConfirm', {
            start: oldStart,
            end: oldEnd,
            oldTimezone,
            newTimezone,
          });

          if (!window.confirm(confirmationMessage)) {
            setLoading(false);
            return;
          }
        }
      }

      if (mode === 'create') {
        await eventService.createEvent(eventData);
        if (onCreated) {
          onCreated();
        } else {
          navigate(`/celebrations/${eventData.celebration_id}`);
        }
      } else if (mode === 'edit' && eventId) {
        await eventService.updateEvent(parseInt(eventId), eventData as UpdateEventDto);
        if (onUpdated) {
          onUpdated(parseInt(eventId, 10));
        } else {
          navigate(`/events/${eventId}`);
        }
      }
    } catch (err: unknown) {
      const apiError = err as ApiError;
      const validationErrors = apiError.response?.data?.details?.validationErrors;
      const message = apiError.response?.data?.message;

      // Handle tags gating - tags must be confirmed before creating events
      if (apiError.response?.status === 403 && validationErrors?.some(
        (validationError) => validationError.constraint === 'tags_gating'
      )) {
        const targetId = parsedCelebrationId || formData.celebration_id;
        setError(t('events:form.errors.tagsRequired'));
        setFieldErrors({ tags_gating: targetId ? `/celebrations/${targetId}/setup` : '' });
      // Handle backend validation errors
      } else if (validationErrors) {
        const backendErrors: Record<string, string> = {};
        validationErrors.forEach((validationError) => {
          backendErrors[validationError.field] = validationError.message;
        });
        setFieldErrors(backendErrors);
        setError(t('events:form.validation.fixErrors'));
      } else if (message?.includes('Maximum of 3')) {
        setError(t('events:form.errors.maxCelebrations'));
      } else if (message?.includes('within celebration date range')) {
        setError(t('events:form.errors.eventOutsideDates'));
      } else if (message?.includes('activities would fall outside')) {
        setError(message);
      } else {
        setError(mode === 'create' ? t('events:form.errors.failedCreate') : t('events:form.errors.failedUpdate'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = <K extends keyof typeof formData>(field: K, value: (typeof formData)[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field-specific and time_range errors when user makes changes
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      if (field === 'start_time' || field === 'end_time') {
        delete newErrors.time_range;
      }
      return newErrors;
    });
  };

  const handleBackNavigation = () => {
    if (embedded && onCancel) {
      onCancel();
      return;
    }

    // Use celebration_id from the form data (loaded event) or URL param
    const targetCelebrationId = parsedCelebrationId || celebrationId || formData.celebration_id;
    
    if (targetCelebrationId) {
      navigate(`/celebrations/${targetCelebrationId}`);
    } else {
      // If no celebration context, go to home or celebrations list
      navigate('/');
    }
  };

  if (loading && mode === 'edit') {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Sparkles className="h-5 w-5 animate-pulse" />
          <span>{t('common:status.loading')}</span>
        </div>
      </div>
    );
  }

  const content = (
    <>
      {/* Compact Header */}
      {!embedded && (
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={handleBackNavigation}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-lg font-bold">
              {mode === 'create' ? t('events:new') : t('events:edit')}
            </h1>
            {celebrationDetails && (
              <p className="text-xs text-muted-foreground">
                Within {formatShortDate(celebrationDetails.start_date)} - {formatShortDate(celebrationDetails.end_date)}
              </p>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
          {fieldErrors.tags_gating && (
            <button
              type="button"
              className="ml-2 text-primary underline hover:text-primary/80 font-medium"
              onClick={() => navigate(fieldErrors.tags_gating)}
            >
              {t('events:form.buttons.completeSetup')}
            </button>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-white rounded-lg border p-4 space-y-4">
          {/* Title */}
          <div>
            <Label htmlFor="title" className="text-xs font-medium">{t('events:form.labels.eventTitle')}</Label>
            <Input
              id="title"
              type="text"
              placeholder={t('events:form.placeholders.eventTitle')}
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              required
              className="mt-1"
            />
          </div>

          {/* Start and End Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="start_time" className="text-xs font-medium">{t('events:form.labels.startTime')}</Label>
              <Input
                id="start_time"
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => handleInputChange('start_time', e.target.value)}
                required
                className="mt-1"
                compact
              />
              {fieldErrors.start_time && (
                <span className="text-xs text-red-600 mt-1">{fieldErrors.start_time}</span>
              )}
            </div>
            <div>
              <Label htmlFor="end_time" className="text-xs font-medium">{t('events:form.labels.endTime')}</Label>
              <Input
                id="end_time"
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => handleInputChange('end_time', e.target.value)}
                required
                className="mt-1"
                compact
              />
              {fieldErrors.end_time && (
                <span className="text-xs text-red-600 mt-1">{fieldErrors.end_time}</span>
              )}
            </div>
          </div>
          
          {/* Cross-field validation error */}
          {fieldErrors.time_range && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {fieldErrors.time_range}
            </div>
          )}

          {/* Max Guests */}
          <div>
            <Label htmlFor="max_guests" className="text-xs font-medium flex items-center gap-1">
              <Users className="h-3 w-3" />
              {t('events:form.labels.maxGuests')}
            </Label>
            <Input
              id="max_guests"
              type="number"
              placeholder="∞"
              value={formData.max_guests || ''}
              onChange={(e) => handleInputChange('max_guests', e.target.value ? parseInt(e.target.value) : undefined)}
              min="1"
              className="mt-1"
              compact
            />
          </div>

          {/* Venue */}
          <div>
            <Label className="text-xs font-medium flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {t('events:form.labels.venue')}
            </Label>
            <div className="mt-1">
              <VenueAutocomplete
                value={selectedVenue}
                onChange={(venue) => {
                  setSelectedVenue(venue);
                  if (venue) {
                    // If it's an already-saved venue, use its DB id directly to skip ensureVenue
                    const dbVenueId = (venue as GooglePlaceWithDbId)._db_venue_id;
                    setFormData(prev => ({ ...prev, venue_id: dbVenueId }));
                    if (dbVenueId) {
                      loadVenueRooms(dbVenueId);
                      loadVenueDetails(dbVenueId)
                        .then((venueDetails) => {
                          setSelectedVenueTimezone(venueDetails?.timezone || 'UTC');
                        })
                        .catch(() => {
                          setSelectedVenueTimezone('UTC');
                        });
                    } else {
                      setVenueRooms([]);
                      setSelectedVenueTimezone('UTC');
                    }
                    setSelectedRoomId(undefined);
                    setSelectedSeatingConfig(undefined);
                  }
                }}
                placeholder={t('events:form.placeholders.venue')}
                className="w-full"
                locationBias={celebrationDetails?.location}
              />
            </div>
            {selectedVenue && (
              <div className="mt-2 p-2 bg-muted/50 rounded text-xs">
                <div className="font-medium">{selectedVenue.name}</div>
                {selectedVenue.formatted_address && (
                  <div className="text-muted-foreground mt-0.5">{selectedVenue.formatted_address}</div>
                )}
              </div>
            )}
          </div>

          {/* Room & Seating Config - shown when venue has rooms */}
          {venueRooms.length > 0 && (
            <div className="space-y-3 p-3 bg-muted/30 rounded-lg border border-dashed">
              <div>
                <Label htmlFor="venue_room" className="text-xs font-medium flex items-center gap-1">
                  <LayoutGrid className="h-3 w-3" />
                  {t('events:form.labels.roomSpace')}
                </Label>
                <select
                  id="venue_room"
                  value={selectedRoomId || ''}
                  onChange={(e) => {
                    const roomId = e.target.value ? parseInt(e.target.value) : undefined;
                    setSelectedRoomId(roomId);
                    setSelectedSeatingConfig(undefined);
                  }}
                  className="mt-1 w-full h-8 px-2 text-sm rounded-md border border-input bg-background"
                  data-testid="room-select"
                >
                  <option value="">{t('events:form.rooms.noRoom')}</option>
                  {venueRooms.map(room => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                      {room.max_capacity ? ` (up to ${room.max_capacity})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Seating Config - shown when a room is selected and has configs */}
              {selectedRoomId && (() => {
                const selectedRoom = venueRooms.find(r => r.id === selectedRoomId);
                if (!selectedRoom || selectedRoom.seating_configs.length === 0) return null;
                return (
                  <div>
                    <Label htmlFor="seating_config" className="text-xs font-medium">{t('events:form.labels.seatingLayout')}</Label>
                    <select
                      id="seating_config"
                      value={selectedSeatingConfig || ''}
                      onChange={(e) => setSelectedSeatingConfig(e.target.value || undefined)}
                      className="mt-1 w-full h-8 px-2 text-sm rounded-md border border-input bg-background"
                      data-testid="seating-config-select"
                    >
                      <option value="">{t('events:form.rooms.noLayout')}</option>
                      {selectedRoom.seating_configs.map(sc => (
                        <option key={sc.config_type} value={sc.config_type}>
                          {SEATING_CONFIG_LABELS[sc.config_type]}
                          {sc.capacity ? ` (${sc.capacity} seats)` : ''}
                          {sc.is_default ? ' ★' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>
          )}

          {/* No rooms available - show helpful message */}
          {(formData.venue_id || selectedVenue) && venueRooms.length === 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <div className="flex items-start gap-2">
                <LayoutGrid className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                <p className="font-medium text-blue-900">{t('events:form.rooms.noRoomsTitle')}</p>
                  <p className="text-xs text-blue-700 mt-1">
                    {t('events:form.rooms.noRoomsHint')}
                  </p>
                  {formData.venue_id && (
                    <a 
                      href={parsedCelebrationId ? `/celebrations/${parsedCelebrationId}/venues/${formData.venue_id}` : `/venues/${formData.venue_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {t('events:form.rooms.manageRooms')} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Description - compact */}
          <div>
            <Label htmlFor="description" className="text-xs font-medium">{t('events:fields.description')}</Label>
            <textarea
              id="description"
              placeholder={t('events:form.placeholders.description')}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:ring-2 focus:ring-ring resize-none"
              rows={2}
            />
          </div>

          {/* Status and Public - inline */}
          <div className="flex items-center justify-between gap-4">
            {mode === 'edit' && (
              <div className="flex-1">
                <Label htmlFor="status" className="text-xs font-medium">{t('events:fields.status')}</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value as EventStatus)}
                  className="mt-1 w-full h-8 px-2 text-sm rounded-md border border-input bg-background"
                >
                  <option value="draft">{t('events:form.statusOptions.draft')}</option>
                  <option value="active">{t('events:form.statusOptions.active')}</option>
                  <option value="completed">{t('events:form.statusOptions.completed')}</option>
                  <option value="cancelled">{t('events:form.statusOptions.cancelled')}</option>
                </select>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                id="is_public"
                type="checkbox"
                checked={formData.is_public}
                onChange={(e) => handleInputChange('is_public', e.target.checked)}
                className="h-4 w-4 text-primary rounded border-gray-300"
              />
              <Label htmlFor="is_public" className="text-xs">{t('events:form.labels.publicEvent')}</Label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleBackNavigation} disabled={loading}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" size="sm" disabled={loading}>
            {loading && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2" />}
            <Save className="h-3 w-3 mr-1" />
            {mode === 'create' ? t('common:actions.create') : t('common:actions.save')}
          </Button>
        </div>
      </form>
    </>
  );

  if (embedded) {
    return <div className="w-full">{content}</div>;
  }

  return (
    <PageContainer width="default" className="space-y-4">
      <AppBreadcrumbs />
      {content}
    </PageContainer>
  );
};
